<template>
  <div class="full-width text-right q-gutter-sm">
    <slot />

    <q-btn-dropdown
      v-if="showDataSourceSelector"
      color="primary-variant"
      size="sm"
      icon="source"
      class="full-width ellipsis"
      outline
      :label="dataSourceLabel"
      @hide="onUpdateFilter"
    >
      <q-list>
        <div
          class="q-pa-xs text-center text-subtitle2 bg-primary-variant text-white"
        >
          {{ $t('insights.chartConfigurator.textDataSource') }}
        </div>
        <q-separator />

        <q-item
          v-for="dataSourceObj in filteredDataSources"
          :key="dataSourceObj.id"
          v-close-popup
          clickable
          :active="modelValue.dataSource === dataSourceObj.id"
          active-class="bg-grey-2 text-bold"
          @click="onSelectDataSource(dataSourceObj.id)"
        >
          <q-item-section>
            <q-item-label>{{ dataSourceObj.label }}</q-item-label>
          </q-item-section>
        </q-item>
      </q-list>
    </q-btn-dropdown>
    <q-btn-dropdown
      color="primary-variant"
      size="sm"
      icon="arrow_right_alt"
      class="full-width ellipsis"
      :disable="!hasDataSource"
      outline
      :label="xAxisLabel"
      @hide="onUpdateFilter"
    >
      <q-list>
        <div
          class="q-pa-xs text-center text-subtitle2 bg-primary-variant text-white"
        >
          {{ $t('insights.chartConfigurator.textXAxis') }}
        </div>
        <q-separator />

        <q-item
          v-for="xAxisObj in dataSourceXAxes"
          :key="xAxisObj.id"
          v-close-popup
          clickable
          :active="modelValue.xAxis === xAxisObj.id"
          active-class="bg-grey-2 text-bold"
          @click="onSelectXAxis(xAxisObj)"
        >
          <q-item-section>
            <q-item-label>{{ xAxisObj.label }}</q-item-label>
          </q-item-section>
          <q-item-section avatar>
            <q-icon
              color="primary"
              :name="chartTypes[xAxisObj.chartType].icon"
            />
          </q-item-section>
        </q-item>
      </q-list>
    </q-btn-dropdown>
    <q-btn-dropdown
      color="primary-variant"
      size="sm"
      outline
      :disable="!hasDataSource"
      class="full-width ellipsis"
    >
      <template #label>
        <q-icon
          name="arrow_right_alt"
          color="primary-variant"
          class="q-mr-sm rotate-270"
        />
        <span class="block ellipsis">{{ currentDataSeriesPresetLabel }}</span>
      </template>
      <q-list>
        <div
          class="q-pa-xs text-center text-subtitle2 bg-primary-variant text-white"
        >
          {{ $t('insights.chartConfigurator.textData') }}
        </div>
        <q-separator />
        <template
          v-for="seriesPreset in dataSeriesPresets"
          :key="seriesPreset.id"
        >
          <div
            v-if="seriesPreset.isGroupHeader"
            class="q-pa-xs text-center text-subtitle2 bg-primary text-white"
          >
            {{ seriesPreset.label }}
          </div>
          <q-item
            v-else
            v-close-popup
            clickable
            :active="seriesPreset.id === modelValue.dataSeriesPreset"
            active-class="bg-grey-2 text-bold"
            @click="onSelectDataSeriesPreset(seriesPreset)"
          >
            <q-item-section>
              <q-item-label>{{ seriesPreset.label }}</q-item-label>
            </q-item-section>
          </q-item>
        </template>
      </q-list>
    </q-btn-dropdown>
    <q-btn-dropdown
      v-if="!hideFilter"
      color="primary-variant"
      size="sm"
      icon="filter_list"
      outline
      class="full-width ellipsis"
      :label="$t('insights.chartConfigurator.filterLabel')"
      :disable="!hasDataSource"
      @hide="onUpdateFilter"
    >
      <q-list>
        <div
          class="q-pa-xs text-center text-subtitle2 bg-primary-variant text-white"
        >
          {{ $t('insights.chartConfigurator.filterTitle') }}
        </div>
        <template
          v-if="hasFilter('programs')"
        >
          <q-expansion-item
            :model-value="hasFiltersApplied('programs')"
            :label="$t('insights.chartConfigurator.expansionItemProgramsLabel')"
            icon="filter_list"
            expand-separator
            dense
            header-class="bg-primary text-left text-subtitle2 text-white"
            expand-icon-class="text-white"
          >
            <q-expansion-item
              v-for="program in sortedPrograms"
              :key="program.id"
              :model-value="filterValue(program.filter, true) === undefined"
              expand-icon-toggle
              expand-separator
              header-class="q-pa-none"
              @show="onShowProgramTargets(program.id)"
            >
              <template #header>
                <q-item
                  tag="label"
                  class="full-width"
                >
                  <q-item-section
                    side
                  >
                    <q-checkbox
                      :model-value="filterValue(program.filter, true)"
                      :disable="isInitProgram(program)"
                      @update:model-value="onSwitchFilter(program.filter, $event)"
                    />
                    <q-tooltip v-if="isInitProgram(program)">
                      {{ $t('insights.chartConfigurator.tooltipLockedProgram') }}
                    </q-tooltip>
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ program.programName }}</q-item-label>
                  </q-item-section>
                </q-item>
              </template>
              <q-list
                v-if="programTargets[program.id]"
              >
                <template
                  v-for="programTarget in programTargets[program.id]"
                  :key="programTarget.id"
                >
                  <q-item
                    v-if="programTarget.subTargets.length === 0"
                    :inset-level="0.5"
                    tag="label"
                  >
                    <q-item-section
                      side
                      top
                    >
                      <q-checkbox
                        :model-value="filterValue(programTarget.filter)"
                        @update:model-value="onSwitchFilter(programTarget.filter, $event)"
                      />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>{{ programTarget.label }}</q-item-label>
                    </q-item-section>
                  </q-item>
                  <!-- Has Sub-targets -->
                  <q-expansion-item
                    v-else
                    :model-value="filterValue(programTarget.filter, true) === undefined"
                    expand-icon-toggle
                    expand-separator
                    header-class="q-pa-none"
                  >
                    <template #header>
                      <q-item
                        tag="label"
                        :inset-level="0.5"
                        class="full-width"
                      >
                        <q-item-section
                          side
                        >
                          <q-checkbox
                            :model-value="filterValue(programTarget.filter, true)"
                            @update:model-value="onSwitchFilter(programTarget.filter, $event)"
                          />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label>{{ programTarget.label }}</q-item-label>
                        </q-item-section>
                      </q-item>
                    </template>
                    <q-list>
                      <template
                        v-for="programSubTarget in programTarget.subTargets"
                        :key="programSubTarget.id"
                      >
                        <q-item
                          :inset-level="1"
                          tag="label"
                        >
                          <q-item-section
                            side
                            top
                          >
                            <q-checkbox
                              :model-value="filterValue(programSubTarget.filter)"
                              @update:model-value="onSwitchFilter(programSubTarget.filter, $event)"
                            />
                          </q-item-section>
                          <q-item-section>
                            <q-item-label>{{ programSubTarget.label }}</q-item-label>
                          </q-item-section>
                        </q-item>
                      </template>
                    </q-list>
                  </q-expansion-item>
                </template>
              </q-list>
            </q-expansion-item>
          </q-expansion-item>
          <q-separator />
        </template>
        <template
          v-if="hasFilter('programTags')"
        >
          <q-expansion-item
            :model-value="hasFiltersApplied('programTags')"
            :label="$t('insights.chartConfigurator.expansionItemProgramTagLabel')"
            icon="filter_list"
            expand-separator
            dense
            header-class="bg-primary text-left text-subtitle2 text-white"
            expand-icon-class="text-white"
          >
            <q-list>
              <q-item>
                <q-item-section>
                  <tag-select
                    v-model="editProgramTags"
                    dense
                    outlined
                    no-empty-label
                    class="full-width"
                  />
                </q-item-section>
              </q-item>
            </q-list>
          </q-expansion-item>
          <q-separator />
        </template>
        <template
          v-if="hasFilter('programTypes')"
        >
          <q-expansion-item
            :model-value="hasFiltersApplied('programTypes')"
            :label="$t('insights.chartConfigurator.expansionItemProgramTypeLabel')"
            icon="filter_list"
            expand-separator
            dense
            header-class="bg-primary text-left text-subtitle2 text-white"
            expand-icon-class="text-white"
          >
            <q-list>
              <q-item
                v-for="programType in programTypes"
                :key="programType.value"
                tag="label"
              >
                <q-item-section
                  side
                  top
                >
                  <q-checkbox
                    :model-value="filterValue(['programTypes', 'programType', programType.value])"
                    @update:model-value="onSwitchFilter(
                      ['programTypes', 'programType', programType.value],
                      $event
                    )"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ $t(programTypeLabelKey(programType.value)) }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-expansion-item>
          <q-separator />
        </template>
        <template
          v-if="hasFilter('targetStatuses')"
        >
          <q-expansion-item
            :model-value="hasFiltersApplied('targetStatuses')"
            :label="$t('insights.chartConfigurator.expansionItemTargetStatusLabel')"
            icon="filter_list"
            expand-separator
            dense
            header-class="bg-primary text-left text-subtitle2 text-white"
            expand-icon-class="text-white"
          >
            <q-list>
              <q-item
                v-for="targetStatus in targetStatuses"
                :key="targetStatus.value"
                tag="label"
              >
                <q-item-section
                  side
                  top
                >
                  <q-checkbox
                    :model-value="filterValue(['targetStatuses', 'targetStatus', targetStatus.value])"
                    @update:model-value="onSwitchFilter(
                      ['targetStatuses', 'targetStatus', targetStatus.value],
                      $event
                    )"
                  />
                </q-item-section>
                <q-item-section side>
                  <q-icon
                    :name="targetStatus.icon"
                    :color="targetStatus.color"
                    size="xs"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ $t('targetStatus_' + targetStatus.value) }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-expansion-item>
          <q-separator />
        </template>
        <template
          v-if="hasFilter('topography')"
        >
          <q-item v-if="filterTopographySpinner">
            <q-item-section>
              <div class="row items-center justify-center q-col-gutter-sm">
                <div><q-spinner /></div>
                <div>{{ $t('common.loading') }}</div>
              </div>
            </q-item-section>
          </q-item>
          <template v-else>
            <q-expansion-item
              v-for="programBehaviourGroup in programBehaviourGroups"
              :key="programBehaviourGroup.id"
              :model-value="hasFiltersApplied(['topography', 'programBehaviourGroupId', programBehaviourGroup.id])"
              :label="programBehaviourGroup.label"
              icon="filter_list"
              expand-separator
              dense
              header-class="bg-primary text-left text-subtitle2 text-white"
              expand-icon-class="text-white"
            >
              <q-list>
                <q-item
                  v-for="programBehaviourItem in programBehaviourGroupItems[programBehaviourGroup.id]"
                  :key="programBehaviourItem.id"
                  tag="label"
                >
                  <q-item-section
                    side
                    top
                  >
                    <q-checkbox
                      :model-value="filterValue(
                        ['topography', 'programBehaviourGroupId',
                         programBehaviourGroup.id,
                         'programBehaviourItemId',
                         programBehaviourItem.id]
                      )"
                      @update:model-value="onSwitchFilter(
                        ['topography',
                         'programBehaviourGroupId',
                         programBehaviourGroup.id,
                         'programBehaviourItemId',
                         programBehaviourItem.id],
                        $event
                      )"
                    />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ programBehaviourItem.label }}</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-expansion-item>
            <q-separator />
          </template>
        </template>
        <template
          v-if="hasFilter('users')"
        >
          <q-expansion-item
            :model-value="hasFiltersApplied('users')"
            :label="$t('insights.chartConfigurator.expansionItemUsersLabel')"
            icon="filter_list"
            expand-separator
            dense
            header-class="bg-primary text-left text-subtitle2 text-white"
            expand-icon-class="text-white"
            @show="onShowUsersFilter"
          >
            <q-item v-if="usersSpinner">
              <q-item-section>
                <div class="row items-center justify-center q-col-gutter-sm">
                  <div><q-spinner /></div>
                  <div>{{ $t('common.loading') }}</div>
                </div>
              </q-item-section>
            </q-item>
            <q-list v-else>
              <q-item
                v-for="user in sortedUsers"
                :key="user.userId"
                tag="label"
              >
                <q-item-section
                  side
                  top
                >
                  <q-checkbox
                    :model-value="filterValue(['users', 'userId', user.userId])"
                    @update:model-value="onSwitchFilter(
                      ['users', 'userId', user.userId],
                      $event
                    )"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ user.userName }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-expansion-item>
        </template>
        <template
          v-if="hasFilter('assessmentTypes')"
        >
          <q-expansion-item
            :default-opened="hasFiltersApplied('assessmentTypes')"
            :label="$t('insights.chartConfigurator.expansionItemAssessmentTypesLabel')"
            icon="filter_list"
            expand-separator
            dense
            header-class="bg-primary text-left text-subtitle2 text-white"
            expand-icon-class="text-white"
            @show="onShowAssessmentTypesFilter"
          >
            <q-item v-if="assessmentTypesSpinner">
              <q-item-section>
                <div class="row items-center justify-center q-col-gutter-sm">
                  <div><q-spinner /></div>
                  <div>{{ $t('common.loading') }}</div>
                </div>
              </q-item-section>
            </q-item>
            <q-list v-else>
              <q-expansion-item
                v-for="assessmentType in assessmentTypes"
                :key="assessmentType.id"
                :model-value="filterValue(
                  ['assessmentTypes', 'assessmentTypeId', assessmentType.id], true
                ) === undefined"
                expand-icon-toggle
                expand-separator
                header-class="q-pa-none"
                @show="onShowAssessmentTypeFilter(assessmentType.id)"
              >
                <template #header>
                  <q-item
                    tag="label"
                    class="full-width"
                  >
                    <q-item-section
                      side
                    >
                      <q-checkbox
                        :model-value="filterValue(['assessmentTypes', 'assessmentTypeId', assessmentType.id], true)"
                        @update:model-value="onSwitchFilter(
                          ['assessmentTypes', 'assessmentTypeId', assessmentType.id], $event
                        )"
                      />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>{{ assessmentType.assessmentTypeName }}</q-item-label>
                      <q-item-label caption>
                        {{ assessmentType.descriptiveName }}
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                </template>
                <q-item v-if="assessmentTypeSpinners[assessmentType.id]">
                  <q-item-section>
                    <div class="row items-center justify-center q-col-gutter-sm">
                      <div><q-spinner /></div>
                      <div>{{ $t('common.loading') }}</div>
                    </div>
                  </q-item-section>
                </q-item>
                <template v-else>
                  <q-expansion-item
                    :default-opened="hasFiltersApplied([
                      'assessmentTypes', 'assessmentTypeId', assessmentType.id, 'assessmentId'
                    ])"
                    :label="$t('insights.chartConfigurator.expansionItemAdministrationsLabel')"
                    icon="filter_list"
                    :header-inset-level="0.5"
                    expand-separator
                    dense
                    header-class="bg-primary text-left text-subtitle2 text-white"
                    expand-icon-class="text-white"
                    @show="onShowAssessmentsFilter(assessmentType.id)"
                  >
                    <q-item v-if="assessmentsSpinner">
                      <q-item-section>
                        <div class="row items-center justify-center q-col-gutter-sm">
                          <div><q-spinner /></div>
                          <div>{{ $t('common.loading') }}</div>
                        </div>
                      </q-item-section>
                    </q-item>
                    <q-list v-else>
                      <q-item
                        v-for="assessment in getAssessments(assessmentType.id)"
                        :key="assessment.id"
                        :inset-level="0.5"
                        tag="label"
                      >
                        <q-item-section
                          side
                          top
                        >
                          <q-checkbox
                            :model-value="filterValue(
                              [
                                'assessmentTypes',
                                'assessmentTypeId',
                                assessmentType.id,
                                'assessmentId',
                                assessment.id
                              ]
                            )"
                            @update:model-value="onSwitchFilter(
                              [
                                'assessmentTypes',
                                'assessmentTypeId',
                                assessmentType.id,
                                'assessmentId',
                                assessment.id
                              ],
                              $event
                            )"
                          />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label>{{ assessment.administrationTitle }}</q-item-label>
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </q-expansion-item>
                  <q-expansion-item
                    v-for="assessmentTypeDimension in getAssessmentTypeDimensions(assessmentType.id)"
                    :key="assessmentTypeDimension.id"
                    :default-opened="hasFiltersApplied([
                      'assessmentTypes', 'assessmentTypeId', assessmentType.id, `dim_${assessmentTypeDimension.id}`,
                    ])"
                    :label="assessmentTypeDimension.label"
                    icon="filter_list"
                    :header-inset-level="0.5"
                    expand-separator
                    dense
                    header-class="bg-primary text-left text-subtitle2 text-white"
                    expand-icon-class="text-white"
                    @show="onShowAssessmentTypeDimensionDataFilter(assessmentTypeDimension)"
                  >
                    <q-item v-if="assessmentDataSpinners[assessmentTypeDimension.id]">
                      <q-item-section>
                        <div class="row items-center justify-center q-col-gutter-sm">
                          <div><q-spinner /></div>
                          <div>{{ $t('common.loading') }}</div>
                        </div>
                      </q-item-section>
                    </q-item>
                    <q-list>
                      <template
                        v-for="fieldOption in assessmentTypeFieldOptionsCache[assessmentTypeDimension.id]"
                        :key="fieldOption"
                      >
                        <q-item
                          :inset-level="0.5"
                          tag="label"
                        >
                          <q-item-section
                            side
                            top
                          >
                            <q-checkbox
                              :model-value="filterValue(
                                [
                                  'assessmentTypes',
                                  'assessmentTypeId',
                                  assessmentType.id,
                                  `dim_${assessmentTypeDimension.id}`,
                                  fieldOption
                                ]
                              )"
                              @update:model-value="onSwitchFilter(
                                [
                                  'assessmentTypes',
                                  'assessmentTypeId',
                                  assessmentType.id,
                                  `dim_${assessmentTypeDimension.id}`,
                                  fieldOption
                                ],
                                $event
                              )"
                            />
                          </q-item-section>
                          <q-item-section>
                            <q-item-label>{{ fieldOption }}</q-item-label>
                          </q-item-section>
                        </q-item>
                      </template>
                    </q-list>
                  </q-expansion-item>
                  <q-expansion-item
                    :default-opened="hasFiltersApplied([
                      'assessmentTypes', 'assessmentTypeId', assessmentType.id, 'fieldId',
                    ])"
                    :label="$t('insights.chartConfigurator.expansionItemAssessmentMetricsLabel')"
                    icon="filter_list"
                    :header-inset-level="0.5"
                    expand-separator
                    dense
                    header-class="bg-primary text-left text-subtitle2 text-white"
                    expand-icon-class="text-white"
                  >
                    <q-list>
                      <template
                        v-for="assessmentTypeMetric in getAssessmentTypeMetrics(assessmentType.id)"
                        :key="assessmentTypeMetric.id"
                      >
                        <q-item
                          :inset-level="0.5"
                          tag="label"
                        >
                          <q-item-section
                            side
                            top
                          >
                            <q-checkbox
                              :model-value="filterValue(
                                [
                                  'assessmentTypes',
                                  'assessmentTypeId',
                                  assessmentType.id,
                                  'fieldId',
                                  assessmentTypeMetric.id,
                                ]
                              )"
                              @update:model-value="onSwitchFilter(
                                [
                                  'assessmentTypes',
                                  'assessmentTypeId',
                                  assessmentType.id,
                                  'fieldId',
                                  assessmentTypeMetric.id,
                                ],
                                $event
                              )"
                            />
                          </q-item-section>
                          <q-item-section>
                            <q-item-label>{{ assessmentTypeMetric.label }}</q-item-label>
                          </q-item-section>
                        </q-item>
                      </template>
                    </q-list>
                  </q-expansion-item>
                </template>
              </q-expansion-item>
            </q-list>
          </q-expansion-item>
          <q-separator />
        </template>
      </q-list>
    </q-btn-dropdown>
    <q-btn-dropdown
      color="primary-variant"
      size="sm"
      outline
      icon="stacked_line_chart"
      :disable="disableGroupBy"
      class="full-width ellipsis"
      :label="groupByLabel"
      @show="onLoadGroupByOptions"
    >
      <q-item v-if="groupBySpinner">
        <q-item-section>
          <div class="row items-center justify-center q-col-gutter-sm">
            <div><q-spinner /></div>
            <div>{{ $t('common.loading') }}</div>
          </div>
        </q-item-section>
      </q-item>
      <q-list v-else>
        <div
          class="q-pa-xs text-center text-subtitle2 bg-primary-variant text-white"
        >
          {{ $t('insights.chartConfigurator.textGroupBy') }}
        </div>
        <q-separator />
        <template
          v-for="groupByOption in cachedGroupByOptions"
          :key="groupByOption.value"
        >
          <q-item
            v-close-popup
            clickable
            :active="groupByOption.value === groupByDataProperty"
            active-class="bg-grey-2 text-bold"
            @click="onSetGroupByDataProperty(groupByOption.value)"
          >
            <q-item-section>
              <q-item-label>{{ groupByOption.label }}</q-item-label>
            </q-item-section>
          </q-item>
        </template>
      </q-list>
    </q-btn-dropdown>
    <div class="self-center full-width">
      <q-btn-toggle
        :model-value="modelValue.chartType"
        :options="chartTypeOptions"
        unelevated
        spread
        size="sm"
        :disable="!hasDataSource"
        @update:model-value="onChangeChartType"
      >
        <template #line>
          <q-tooltip>{{ $t('insights.chartConfigurator.chartTypeLine') }}</q-tooltip>
        </template>
        <template #column>
          <q-tooltip>{{ $t('insights.chartConfigurator.chartTypeColumn') }}</q-tooltip>
        </template>
        <template #stacked>
          <q-tooltip>{{ $t('insights.chartConfigurator.chartTypeStacked') }}</q-tooltip>
        </template>
        <template #logarithmic>
          <q-tooltip>{{ $t('insights.chartConfigurator.chartTypeLogarithmic') }}</q-tooltip>
        </template>
        <template #scatterchart>
          <q-tooltip>{{ $t('insights.chartConfigurator.chartTypeScatterchart') }}</q-tooltip>
        </template>
        <template #scatterplot>
          <q-tooltip>{{ $t('insights.chartConfigurator.chartTypeHeatmap') }}</q-tooltip>
        </template>
      </q-btn-toggle>
    </div>
    <template v-if="!hideAnnotations">
      <q-btn-dropdown
        v-if="!showAnnotationsDropdown"
        v-model="showAnnotationsDropdownHandler"
        :loading="phaseLineAnnotationsLoading"
        color="primary-variant"
        size="sm"
        menu-self="bottom end"
        :disable="!hasDataSource"
        outline
        icon="comment"
        class="full-width ellipsis"
        :label="$t('insights.chartConfigurator.textAnnotations')"
      />
      <q-btn-dropdown
        v-else
        v-model="showAnnotationsDropdown"
        color="primary-variant"
        size="sm"
        menu-self="bottom end"
        outline
        icon="comment"
        :disable="!hasDataSource"
        class="full-width ellipsis"
        :label="$t('insights.chartConfigurator.textAnnotations')"
      >
        <div
          class="q-pa-xs text-center text-subtitle2 bg-primary-variant text-white"
        >
          {{ $t('insights.chartConfigurator.textAnnotations') }}
        </div>
        <q-separator />
        <q-expansion-item
          :model-value="true"
          :label="$t('insights.chartConfigurator.expansionItemAnalysis')"
          expand-separator
          dense
          header-class="bg-primary text-left text-subtitle2 text-white"
          expand-icon-class="text-white"
        >
          <q-list>
            <q-item
              v-ripple="!isTrendlineDisabled"
              tag="label"
              :disable="isTrendlineDisabled"
              :clickable="!isTrendlineDisabled"
            >
              <q-item-section avatar>
                <q-toggle
                  v-model="analysisTrendline"
                  color="positive"
                  :disable="isTrendlineDisabled"
                  dense
                />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ $t('insights.chartConfigurator.toggleTrendline') }}</q-item-label>
                <q-item-label
                  v-if="isTrendlineDisabled"
                  caption
                >
                  {{ $t('insights.chartConfigurator.textTrendlineTooltip') }}
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item
              v-ripple
              tag="label"
              clickable
            >
              <q-item-section avatar>
                <q-toggle
                  v-model="analysisAverage"
                  color="positive"
                  dense
                />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ $t('insights.chartConfigurator.toggleAverage') }}</q-item-label>
              </q-item-section>
            </q-item>
            <q-item
              v-ripple
              tag="label"
              :disable="isProgramMasteryCriteriaDisabled"
              :clickable="!isProgramMasteryCriteriaDisabled"
            >
              <q-item-section avatar>
                <q-toggle
                  v-model="progressProgramMasteryCriteria"
                  :disable="isProgramMasteryCriteriaDisabled"
                  color="positive"
                  dense
                />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ $t('insights.chartConfigurator.toggleMasteryCriteria') }}</q-item-label>
                <q-item-label
                  v-if="isProgramMasteryCriteriaDisabled"
                  caption
                >
                  {{ $t('insights.chartConfigurator.textMasteryCriteriaTooltip') }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-expansion-item>
        <q-expansion-item
          :model-value="true"
          :label="$t('insights.chartConfigurator.expansionItemPhaseLines')"
          expand-separator
          dense
          header-class="bg-primary text-left text-subtitle2 text-white"
          expand-icon-class="text-white"
        >
          <q-list>
            <q-item
              v-ripple
              tag="label"
              clickable
            >
              <q-item-section avatar>
                <q-toggle
                  v-model="showProgramStatusPhaseLines"
                  color="positive"
                  dense
                />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ $t('insights.chartConfigurator.toggleProgramStatusChange') }}</q-item-label>
              </q-item-section>
            </q-item>
            <chart-line-group
              v-for="lineGroup in lineGroups"
              :key="lineGroup.key"
              :title="lineGroup.title"
              :add-label="lineGroup.addLabel"
              :switch-label="lineGroup.switchLabel"
              :local-lines="lineGroup.localLines"
              :scopes="lineGroup.scopes"
              :can-edit="hasTherapistCredentials"
              @edit="lineGroup.onEdit"
              @add="lineGroup.onAdd"
              @toggle-scope="(scope, isSelected) => onSelectLineScope(
                lineGroup.lineType, scope.scope, scope.program, scope.target, isSelected,
              )"
              @load-scope="onLoadPhaseLines"
            />
          </q-list>
        </q-expansion-item>
      </q-btn-dropdown>
      <q-btn-dropdown
        v-if="hasAdditionalSettings"
        color="primary-variant"
        size="sm"
        outline
        icon="visibility"
        class="full-width ellipsis"
        :disable="!hasDataSource"
        :label="$t('insights.chartConfigurator.dataVisibility')"
      >
        <q-list>
          <div
            class="q-pa-xs text-center text-subtitle2 bg-primary-variant text-white"
          >
            {{ $t('insights.chartConfigurator.dataVisibility') }}
          </div>
          <q-separator />
          <template
            v-for="setting in additionalSettings"
            :key="setting.id"
          >
            <q-item
              clickable
              class="row"
              active-class="bg-grey-2 text-bold"
              @click="onToggleAdditionalSettings(setting.id)"
            >
              <q-item-section class="col-3">
                <q-toggle
                  v-model="additionalSettingsValues[setting.id]"
                  @click.stop
                  @update:model-value="onSetAdditionalSettings"
                />
              </q-item-section>
              <q-item-section class="col-9">
                <q-item-label>{{ setting.label }}</q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-list>
      </q-btn-dropdown>
    </template>
  </div>
</template>
<script>
import { measurementMetrics, programTypes, targetStatuses } from 'boot/globals';
import ChartLineGroup from 'components/Insights/ChartLineGroup';
import TagSelect from 'components/Tags/TagSelect';
import { Timestamp } from 'firebase/firestore';
import {
  chain, cloneDeep, every, filter, find, flatten, forEach, get, has, isArray, isEmpty, isEqual,
  isObject, keyBy, map, mapValues, merge, mergeWith, omit, omitBy, pick, reduce, reject, set, size,
  sortBy, toLower, unset,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import {
  applyLineChange, matchesLineScope, SCOPED_EVENT_LINES, SCOPED_PHASE_LINES, setLineScopeSwitch,
} from 'src/components/Insights/chartLines';
import Insights from 'src/mixins/Insights';
import Interactions from 'src/mixins/Interactions';
import { assessments as assessmentsStore } from 'src/store/assessments';
import { auth } from 'src/store/auth';
import { programBehaviourData } from 'src/store/programBehaviourData';
import { programs } from 'src/store/programs';
import { programsTargets } from 'src/store/programsTargets';
import { getProgramTypeLabelKey } from 'src/utils/ProgramUtils';
import { defineAsyncComponent } from 'vue';

const DEFAULT_SETTINGS = {
  hideDeletedTargets: false,
  hideBelowMinTrials: false,
};

export default {
  name: 'ChartConfigurator',

  components: { ChartLineGroup, TagSelect },

  mixins: [Insights, Interactions],

  props: {
    modelValue: {
      type: Object,
      required: true,
    },

    chartPhaseLineScopes: {
      type: Array,
      required: false,
      default: () => [],
    },

    initProgramId: {
      type: String,
      default: null,
    },

    initDraftProgramTargets: {
      type: Array,
      default: () => [],
    },

    usersFunc: {
      type: Function,
      required: false,
      default: () => [],
    },

    overridePrograms: {
      type: Array,
      default: null,
    },

    enableMinTrialsFilter: {
      type: Boolean,
      default: false,
    },

    hideFilter: {
      type: Boolean,
      default: false,
    },

    hideAnnotations: {
      type: Boolean,
      default: false,
    },

    filterDataSourceIds: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['update:modelValue', 'refresh-phase-lines'],

  data() {
    return {
      filterTopographySpinner: false,
      usersSpinner: false,
      assessmentTypesSpinner: false,
      assessmentTypeSpinners: {},
      assessmentsSpinner: false,
      assessmentDataSpinners: {},
      groupBySpinner: false,

      programTargets: {},
      sortedUsers: [],
      assessmentTypeFieldsCache: {},
      assessmentTypeFieldOptionsCache: {},
      cachedGroupByOptions: [],

      localFilterTree: {},

      groupByDataProperty: null,

      excludeFlatFilters: ['topography'],

      phaseLineScopes: [],

      showAnnotationsDropdown: false,

      phaseLineAnnotationsLoading: false,

      additionalSettingsValues: { ...DEFAULT_SETTINGS },

      initialPostloadFilter: null,
    };
  },

  computed: {
    ...mapState(programs, { programs: 'visiblePrograms' }),
    ...mapState(assessmentsStore, [
      'assessments', 'assessmentTypes', 'assessmentTypeFieldGroups', 'assessmentTypeFields',
    ]),
    ...mapState(auth, ['hasTherapistCredentials']),
    ...mapState(programBehaviourData, ['programBehaviourGroups', 'programBehaviourGroupItems']),

    hasAdditionalSettings() {
      return this.additionalSettings.length > 0;
    }, // hasAdditionalSettings()

    isTrendlineDisabled() {
      return this.modelValue.xAxis !== 'xaSession';
    }, // isTrendlineDisabled()

    isProgramMasteryCriteriaDisabled() {
      // Disable mastery criteria if multiple programs are selected
      // or data source is not session. Or if Y-axis does not
      // match program measurement metric
      if (this.modelValue.dataSource !== 'dsSessionData'
        || this.modelValue.postloadFilter?.programs?.programId?.length !== 1) {
        return true;
      }

      // Disable mastery criteria if measurement metric does not match graph settings
      const programId = this.modelValue.postloadFilter?.programs?.programId[0];
      const program = this.programs.find((p) => p.id === programId);
      if (program?.primaryMetric) {
        const dataSeriesPreset = measurementMetrics.find((mm) => mm.id === program?.primaryMetric)?.dataSeriesPreset;
        if (dataSeriesPreset !== this.modelValue?.dataSeriesPreset) {
          return true;
        }
      }

      return false;
    }, // isProgramMasteryCriteriaDisabled()

    disableGroupBy() {
      return get(this.chartTypes, [get(this.modelValue, 'chartType'), 'disableGroupBy']);
    }, // disableGroupBy()

    chartPrograms() {
      return reject(this.overridePrograms || this.programs, { type: 'behaviour' }).map((program) => {
        return { ...program, filter: ['programs', 'programId', program.id] };
      });
    }, // chartPrograms()

    sortedPrograms() {
      return sortBy(this.chartPrograms, 'programName');
    }, // sortedPrograms()

    chartTypeOptions() {
      const disabledChartTypes = chain(this.dataSeriesPresetSelected)
        .get('disableChartTypes', [])
        .concat(
          get(this.xAxisSelected, 'disableChartTypes', []),
        ).value();

      return [
        {
          icon: 'show_chart', value: 'line', slot: 'line', disable: disabledChartTypes.includes('line'),
        },
        {
          icon: 'bar_chart', value: 'column', slot: 'column', disable: disabledChartTypes.includes('column'),
        },
        {
          icon: 'stacked_bar_chart',
          value: 'stacked',
          slot: 'stacked',
          disable: disabledChartTypes.includes('stacked'),
        },
        {
          icon: 'trending_up',
          value: 'logarithmic',
          slot: 'logarithmic',
          disable: disabledChartTypes.includes('logarithmic'),
        },
        {
          icon: 'scatter_plot',
          value: 'scatterchart',
          slot: 'scatterchart',
          disable: disabledChartTypes.includes('scatterchart'),
        },
        {
          icon: 'fas fa-fire',
          value: 'scatterplot',
          slot: 'scatterplot',
          disable: disabledChartTypes.includes('scatterplot'),
        },
      ];
    }, // chartTypeOptions()

    dataSource() {
      return find(this.dSources, { id: this.modelValue.dataSource });
    }, // dataSource()

    hasDataSource() {
      return isObject(this.dataSource);
    }, // hasDataSource()

    dataSourceLabel() {
      return get(this.dataSource, 'label', this.$t('insights.chartConfigurator.sourcesDefaultLabel'));
    }, // dataSourceLabel()

    dataSeriesPresets() {
      return get(this.dataSource, 'seriesPresets', []);
    }, // dataSeriesPresets()

    dataSourceXAxes() {
      return get(this.dataSource, 'xAxes', []);
    }, // dataSourceXAxes()

    additionalSettings() {
      const additionalSettings = get(this.dataSource, 'additionalSettings', []);

      if (this.enableMinTrialsFilter) {
        return additionalSettings;
      }

      return additionalSettings.filter((setting) => setting.id !== 'hideBelowMinTrials');
    }, // dataSourceAdditionalSettings()

    xAxisLabel() {
      return chain(this.dataSourceXAxes)
        .find({ id: this.modelValue.xAxis })
        .get('label', this.$t('insights.chartConfigurator.textXAxis'))
        .value();
    }, // xAxisLabel()

    dataSeriesPresetSelected() {
      return find(this.dataSeriesPresets, { id: this.modelValue.dataSeriesPreset });
    }, // dataSeriesPresetSelected()

    xAxisSelected() {
      return find(this.dataSourceXAxes, { id: this.modelValue.xAxis });
    }, // xAxisSelected()

    currentDataSeriesPresetLabel() {
      return get(this.dataSeriesPresetSelected, 'label', this.$t('insights.chartConfigurator.seriesDefault'));
    }, // currentDataSeriesPresetLabel()

    groupByLabel() {
      if (this.modelValue?.groupByLabel) {
        return this.modelValue.groupByLabel;
      }
      return chain(this.dataSource)
        .get('groupByOptions')
        .find({ value: this.groupByDataProperty })
        .get('label')
        .defaultTo(
          chain(this.cachedGroupByOptions)
            .find({ value: this.groupByDataProperty })
            .get('label')
            .defaultTo(this.$t('insights.chartConfigurator.textGroupBy'))
            .value(),
        )
        .value();
    },

    targetStatuses() {
      return targetStatuses;
    }, // targetStatuses()

    programTypes() {
      return programTypes;
    }, // programTypes()

    assessmentTypeDimensions() {
      return [];
    }, // assessmentTypeDimensions()

    assessmentTypeDimensionData() {
      return [];
    }, // assessmentTypeDimensionData()

    phaseLines() {
      return chain(this.modelValue)
        .get('phaseLines', [])
        .orderBy('startTimestamp')
        .value();
    }, // phaseLines()

    localPhaseLines() {
      return filter(this.phaseLines, (phaseLine) => { return get(phaseLine, 'scope', 'local') === 'local'; });
    }, // localPhaseLines()

    eventLines() {
      return [...(this.modelValue.eventLines ?? [])]
        .sort((a, b) => a.startTimestamp.toDate() - b.startTimestamp.toDate());
    }, // eventLines()

    lineGroups() {
      return [
        {
          key: 'phase',
          title: this.$t('insights.chartConfigurator.textPhaseLines'),
          lineType: SCOPED_PHASE_LINES,
          localLines: this.localPhaseLines,
          scopes: this.getLineGroupScopes(SCOPED_PHASE_LINES),
          addLabel: this.$t('insights.chartConfigurator.textAddPhaseLine'),
          switchLabel: (label) => this.$t('insights.chartConfigurator.toggleShowPhaseLine', { label }),
          onAdd: this.onAddPhaseLine,
          onEdit: this.onEditPhaseLine,
        },
        {
          key: 'event',
          title: this.$t('insights.chartConfigurator.textEventLines'),
          lineType: SCOPED_EVENT_LINES,
          localLines: this.eventLines,
          scopes: this.getLineGroupScopes(SCOPED_EVENT_LINES),
          addLabel: this.$t('insights.chartConfigurator.textAddEventLine'),
          switchLabel: (label) => this.$t('insights.chartConfigurator.toggleShowEventLine', { label }),
          onAdd: this.onAddEventLine,
          onEdit: this.onEditEventLine,
        },
      ];
    }, // lineGroups()

    editProgramTags: {
      get() {
        return chain(this.localFilterTree)
          .get(['programTags', 'programTag'])
          .keys()
          .defaultTo([])
          .value();
      },
      set(tags) {
        const programTag = chain(tags)
          .sortBy(toLower)
          .keyBy((tag) => tag)
          .mapValues(() => ({}))
          .value();

        this.localFilterTree = { ...this.localFilterTree, programTags: { programTag } };
      },
    }, // editProgramTags()

    analysisTrendline: {
      get() {
        return chain(this.modelValue)
          .get(['analysis', 'trendline'], false)
          .value();
      },
      set(trendline) {
        this.$nextTick(() => {
          this.$emit('update:modelValue', {
            ...this.modelValue,
            analysis: { ...get(this.modelValue, 'analysis', {}), trendline },
          });
        });
      },
    }, // analysisTrendline()

    analysisAverage: {
      get() {
        return chain(this.modelValue)
          .get(['analysis', 'average'], false)
          .value();
      },
      set(average) {
        this.$nextTick(() => {
          this.$emit('update:modelValue', {
            ...this.modelValue,
            analysis: { ...get(this.modelValue, 'analysis', {}), average },
          });
        });
      },
    }, // analysisAverage()

    progressProgramMasteryCriteria: {
      get() {
        return this.modelValue?.analysis?.programMasteryCriteria || false;
      },
      set(programMasteryCriteria) {
        this.$nextTick(() => {
          this.$emit('update:modelValue', {
            ...this.modelValue,
            analysis: { ...get(this.modelValue, 'analysis', {}), programMasteryCriteria },
          });
        });
      },
    }, // progressProgramMasteryCriteria()

    showProgramStatusPhaseLines: {
      get() {
        return this.modelValue?.analysis?.programStatusPhaseLines !== false;
      },
      set(programStatusPhaseLines) {
        this.$nextTick(() => {
          this.$emit('update:modelValue', {
            ...this.modelValue,
            analysis: { ...get(this.modelValue, 'analysis', {}), programStatusPhaseLines },
          });
        });
      },
    }, // showProgramStatusPhaseLines()

    showAnnotationsDropdownHandler: {
      get() {
        return this.showAnnotationsDropdown;
      },
      async set(value) {
        if (!value) {
          this.showAnnotationsDropdown = value;
          return;
        }

        await this.onUpdatePhaseLineAnnotations();
        this.showAnnotationsDropdown = value;
      },
    }, // showAnnotationsDropdownHandler()

    showDataSourceSelector() {
      return this.filteredDataSources.length > 1;
    },

    filteredDataSources() {
      if (this.filterDataSourceIds.length === 0) {
        return this.dSources;
      }

      return this.dSources.filter((dataSource) => (
        this.filterDataSourceIds.includes(dataSource.id)
      ));
    },

  },

  watch: {
    modelValue: {
      immediate: true,
      deep: true,
      async handler(value) {
        if (!this.initialPostloadFilter) {
          this.initialPostloadFilter = value.postloadFilter;
        }
        this.groupByDataProperty = value.groupByDataProperty;

        const savedProgramIds = get(value, ['postloadFilter', 'programs', 'programId'], []);
        const programIds = isArray(savedProgramIds) ? savedProgramIds : [];

        // A program-specific graph must always include the program it was opened for.
        // Existing/rebuilt reports may have a saved program filter that is empty, missing or malformed.
        // The uncheck guard alone can't fix that, and the checkbox would be disabled while unchecked with
        // the graph running unconstrained. Self-heal by emitting a filter that re-adds it; the watcher
        // re-runs with the corrected value and rebuilds the tree so the checkbox shows as checked.
        const initialProgramRepair = this.buildInitialProgramRepair(value);
        if (initialProgramRepair) {
          this.$emit('update:modelValue', initialProgramRepair);
          return;
        }

        const analysisUpdates = {};
        // Make sure we don't allow mastery criteria when they are not valid
        if (this.isProgramMasteryCriteriaDisabled && value.analysis?.programMasteryCriteria) {
          analysisUpdates.programMasteryCriteria = false;
        }
        if (!isEmpty(analysisUpdates)) {
          this.$emit('update:modelValue', {
            ...value,
            analysis: {
              ...value.analysis,
              ...analysisUpdates,
            },
          });
        }

        this.additionalSettingsValues = {
          ...DEFAULT_SETTINGS,
          ...(value.additionalSettings || {}),
        };

        if (has(value, '_localFilterUpdate') && !programIds.includes('null')) {
          return;
        }
        // Convert flat filter to filter tree
        this.localFilterTree = mapValues(
          value.postloadFilter,
          (filterGroup, filterGroupKey) => chain(filterGroup)
            .omit('targetId')
            .omit('parentTargetId')
            .mapValues((filterItems) => {
              if (this.excludeFlatFilters.includes(filterGroupKey)) {
                return cloneDeep(filterItems);
              }

              return reduce(
                filterItems,
                (result, filterItem) => {
                  return merge(result, { [filterItem]: {} });
                },
                {},
              );
            })
            .value(),
        );

        // If we are deep filtering targets then we have to find their corresponding program
        // For now we have to load all targets to do that
        const targetIds = get(value.postloadFilter, ['programs', 'targetId']);

        if (!isEmpty(targetIds)) {
          const programTargetsActions = chain(this.chartPrograms)
            .map(async (program) => {
              const targets = await this.getTargets(program.id);
              const targetId = chain(targets)
                .reject('parentTarget')
                .filter((target) => targetIds.includes(target.id))
                .keyBy('id')
                .mapValues(() => ({ }))
                .value();

              const parentTargetId = chain(targets)
                .filter((target) => (target.parentTarget && targetIds.includes(target.id)))
                .groupBy('parentTarget')
                .mapValues((subTargets) => ({ targetId: chain(subTargets).keyBy('id').mapValues(() => ({})).value() }))
                .value();

              if (isEmpty(targetId) && isEmpty(parentTargetId)) {
                return null;
              }

              return {
                program: program.id,
                ...(isEmpty(targetId) ? {} : { targetId }),
                ...(isEmpty(parentTargetId) ? {} : { parentTargetId }),
              };
            })
            .value();
          const programsAndTargets = await Promise.all(programTargetsActions);
          const programsObj = chain(programsAndTargets)
            .compact()
            .keyBy('program')
            .mapValues((program) => omit(program, 'program'))
            .value();

          this.localFilterTree = merge(this.localFilterTree, { programs: { programId: programsObj } });
        }

        // If we are deep filtering sub-targets then we have to find their corresponding parent
        // program and parent target
        // For now we have to load all targets to do that
        const parentTargetIds = get(value.postloadFilter, ['programs', 'parentTargetId']);

        if (!isEmpty(parentTargetIds)) {
          const programTargetsActions = chain(this.chartPrograms)
            .map(async (program) => {
              const targets = await this.getTargets(program.id);
              const retVal = chain(targets)
                .filter((target) => parentTargetIds.includes(target.id))
                .keyBy('id')
                .mapValues(() => ({ }))
                .value();

              if (isEmpty(retVal)) {
                return null;
              }

              return { program: program.id, parentTargetId: retVal };
            })
            .value();
          const programsAndTargets = await Promise.all(programTargetsActions);
          const programsObj = chain(programsAndTargets)
            .compact()
            .keyBy('program')
            .mapValues((program) => omit(program, 'program'))
            .value();
          this.localFilterTree = merge(this.localFilterTree, { programs: { programId: programsObj } });
        }

        // Move assessment fields back under assessment types
        const fieldIds = get(value.postloadFilter, ['assessmentTypes', 'fieldId']);
        const assessmentTypeIds = get(value.postloadFilter, ['assessmentTypes', 'assessmentTypeId']);
        if (!isEmpty(fieldIds)) {
          // Load assessment type fields if they are not already loaded
          // eslint-disable-next-line no-restricted-syntax
          for (const assessmentTypeId of assessmentTypeIds) {
            if (isEmpty(this.assessmentTypeFieldsCache[assessmentTypeId])) {
              // eslint-disable-next-line no-await-in-loop
              await this.cacheAssessmentTypeFields(assessmentTypeId);
            }
          }

          // Find corresponding assessment type to each field and map them up
          const assessmentTypeObj = assessmentTypeIds.reduce((acc, assessmentTypeId) => {
            const fields = this.assessmentTypeFieldsCache[assessmentTypeId]
              .filter((field) => fieldIds.includes(field.id))
              .map(({ id }) => [id, {}]);

            if (fields.length === 0) {
              return acc;
            }

            const fieldsObj = Object.fromEntries(fields);

            if (acc[assessmentTypeId]) {
              acc[assessmentTypeId].fieldId = fieldsObj;
            } else {
              acc[assessmentTypeId] = { fieldId: fieldsObj };
            }

            return acc;
          }, this.localFilterTree?.assessmentTypes?.assessmentTypeId ?? {});

          this.localFilterTree = merge(
            this.localFilterTree,
            { assessmentTypes: { assessmentTypeId: assessmentTypeObj } },
          );
          delete this.localFilterTree.assessmentTypes.fieldId;
        } // Assessment Field IDs

        // Move assessments back under assessment types
        const assessmentIds = get(value.postloadFilter, ['assessmentTypes', 'assessmentId']);
        if (!isEmpty(assessmentIds)) {
          // Load assessment type fields if they are not already loaded
          await this.cacheAssessments();
          const assessmentObj = assessmentTypeIds
            .reduce((acc, assessmentTypeId) => {
              const assessments = chain(this.assessments)
                .filter((assessment) => (
                  assessmentIds.includes(assessment.id)
                  && assessment.assessmentType === assessmentTypeId))
                .map(({ id }) => ([id, {}]))
                .fromPairs()
                .value();
              if (isEmpty(assessments)) {
                return acc;
              }
              if (acc[assessmentTypeId]) {
                acc[assessmentTypeId].assessmentId = assessments;
                return acc;
              }
              return {
                ...acc,
                [assessmentTypeId]: {
                  assessmentId: assessments,
                },
              };
            }, this.localFilterTree?.assessmentTypes?.assessmentTypeId ?? {});

          this.localFilterTree = merge(
            this.localFilterTree,
            { assessmentTypes: { assessmentTypeId: assessmentObj } },
          );
          delete this.localFilterTree.assessmentTypes.assessmentId;
        } // Assessment IDs

        const assessmentDimensions = chain(value.postloadFilter)
          .get(['assessmentTypes'])
          .pickBy((v, k) => k.startsWith('dim_'))
          .value();

        if (!isEmpty(assessmentDimensions)) {
          const assessmentDimensionIds = chain(assessmentDimensions)
            .keys()
            .map((v) => v.replace('dim_', ''))
            .value();

          // Load assessment type fields if they are not already loaded
          // eslint-disable-next-line no-restricted-syntax
          for (const assessmentTypeId of assessmentTypeIds) {
            if (isEmpty(this.assessmentTypeFieldsCache[assessmentTypeId])) {
              // eslint-disable-next-line no-await-in-loop
              await this.cacheAssessmentTypeFields(assessmentTypeId);
            }
          }

          // Find corresponding assessment type to each dimension field and map them up
          const assessmentTypeObj = assessmentTypeIds
            .reduce((acc, assessmentTypeId) => {
              const dimensionFields = chain(this.assessmentTypeFieldsCache[assessmentTypeId])
                .filter((field) => assessmentDimensionIds.includes(field.id))
                .map(({ id }) => ([
                  `dim_${id}`,
                  chain(assessmentDimensions[`dim_${id}`])
                    .map((fieldOption) => ([fieldOption, {}]))
                    .fromPairs()
                    .value(),
                ]))
                .fromPairs()
                .value();
              if (isEmpty(dimensionFields)) {
                return acc;
              }
              if (acc[assessmentTypeId]) {
                acc[assessmentTypeId] = {
                  ...acc[assessmentTypeId],
                  ...dimensionFields,
                };
                return acc;
              }
              return {
                ...acc,
                [assessmentTypeId]: dimensionFields,
              };
            }, this.localFilterTree?.assessmentTypes?.assessmentTypeId ?? {});

          this.localFilterTree = merge(
            this.localFilterTree,
            { assessmentTypes: { assessmentTypeId: assessmentTypeObj } },
          );
          assessmentDimensionIds.forEach((dimId) => {
            delete this.localFilterTree.assessmentTypes[`dim_${dimId}`];
          });

          const assessmentTypeDimensionActions = assessmentDimensionIds
            .map((assessmentTypeDimensionId) => (this.cacheAssessmentTypeFieldOptions(assessmentTypeDimensionId)));

          await Promise.all(assessmentTypeDimensionActions);
        } // Assessment Dimension Field IDs
      },
    }, // modelValue
  },

  methods: {
    ...mapActions(programsTargets, ['getAllProgramTargets']),
    ...mapActions(assessmentsStore, [
      'loadAssessments', 'loadAssessmentTypes', 'getAssessmentFormFields', 'readDataFieldOptions',
    ]),

    programTypeLabelKey(programType) {
      return getProgramTypeLabelKey(programType, this.currentClient?.type);
    },

    onSelectDataSource(dataSource) {
      if (dataSource === this.modelValue.dataSource) {
        return;
      }

      this.cachedGroupByOptions = [];
      const selectedDataSource = find(this.dSources, { id: dataSource });
      const firstSeriesPreset = chain(selectedDataSource)
        .get('seriesPresets')
        .find((preset) => !preset.isGroupHeader)
        .value();

      const firstXAxisPreset = chain(selectedDataSource)
        .get('xAxes')
        .head()
        .value();

      const availableFilters = get(selectedDataSource, 'availableFilters');
      const postLoadFilterData = this.modelValue.postloadFilter;
      // 'onSelectDataSource' mutates 'postloadFilter' and we can end up with a filter that will load all programs
      // this specifically checks for a `programs` filter present on switching data sources
      if (!postLoadFilterData.programs?.programId?.length && availableFilters.includes('programs')) {
        postLoadFilterData.programs = this.initialPostloadFilter?.programs;
      }
      this.localFilterTree = pick(this.localFilterTree, availableFilters);
      // Make sure we don't filter on data that this data source doesn't support
      const newPostloadFilter = availableFilters.reduce((acc, availableFilter) => {
        if (postLoadFilterData[availableFilter]) {
          acc[availableFilter] = postLoadFilterData[availableFilter];
        }
        return acc;
      }, {});
      this.$emit('update:modelValue', {
        ...this.modelValue,
        postloadFilter: newPostloadFilter,
        groupByLabel: null,
        groupByDataProperty: null,
        groupBySeriesLabelFrom: null,
        arrayGrouping: null,
        dataSeriesPreset: get(firstSeriesPreset, 'id', null),
        xAxis: get(firstXAxisPreset, 'id', null),
        ...omit(firstXAxisPreset, 'id'),
        ...omit(firstSeriesPreset, 'id'),
        dataSource,
        analysis: {
          trendline: false,
          average: false,
          programMasteryCriteria: false,
          programStatusPhaseLines: true,
        },
      });
    }, // onSelectDataSource()

    onSelectDataSeriesPreset(seriesPreset) {
      if (isEmpty(seriesPreset)) {
        return;
      }

      this.$emit('update:modelValue', {
        ...this.modelValue,
        dataSeriesPreset: get(seriesPreset, 'id', null),
        ...omit(seriesPreset, 'id'),
      });
    }, // onSelectDataSeriesPreset()

    onSelectXAxis(xAxis) {
      if (isEmpty(xAxis)) {
        return;
      }

      this.$emit('update:modelValue', {
        ...this.modelValue,
        xAxis: get(xAxis, 'id', null),
        ...omit(xAxis, ['id', 'label']),
        analysis: {
          ...get(this.modelValue, 'analysis', {}),
          trendline: false,
          average: false,
        },
      });
    }, // onSelectXAxis()

    onChangeChartType(chartType) {
      let groupBy = {};

      if (get(this.chartTypes, [chartType, 'disableGroupBy'])) {
        groupBy = {
          groupByDataProperty: null,
          groupBySeriesLabelFrom: null,
        };
      }

      this.$emit('update:modelValue', {
        ...this.modelValue,
        ...groupBy,
        chartType,
      });
    }, // onChangeChartType()

    onSetGroupByDataProperty(groupByDataProperty) {
      const groupByProp = this.cachedGroupByOptions
        .find((cachedGroupByOption) => (cachedGroupByOption.value === groupByDataProperty));
      const groupBySeriesLabelFrom = groupByProp?.seriesLabelFrom ?? null;
      const retVal = {
        ...this.modelValue,
        groupByDataProperty,
        groupBySeriesLabelFrom,
        groupBySeriesOrderBy: groupByProp?.seriesOrderBy ?? groupBySeriesLabelFrom,
        groupByLabel: groupByProp?.label,
      };
      this.$emit('update:modelValue', retVal);
    }, // onSetGroupByDataProperty()

    onSetAdditionalSettings() {
      const newAdditionalSettings = this.enableMinTrialsFilter
        ? { ...this.additionalSettingsValues }
        : omit(this.additionalSettingsValues, 'hideBelowMinTrials');

      this.$emit('update:modelValue', {
        ...this.modelValue,
        additionalSettings: newAdditionalSettings,
      });
    },

    /**
     * @param {string} settingId
     */
    onToggleAdditionalSettings(settingId) {
      this.additionalSettingsValues[settingId] = !this.additionalSettingsValues[settingId];
      this.onSetAdditionalSettings();
    },

    /**
     * I can hardly try to describe this filtering function
     * It basically flattens out the filter tree and returns
     * an object of arrays of the flattened properties. If a branch
     * contains any elements the branch itself should not be included in
     * the result as the filter is an working as a logical OR filter
     */
    onUpdateFilter() {
      const flattenTreeFunc = (tree, initialObj = {}) => reduce(tree, (output, treeItems, key) => {
        const treeItemKeys = [];
        forEach(treeItems, (treeItem, treeItemKey) => {
          if (has(treeItems, null)) {
            treeItemKeys.push(null);
          } else {
            treeItemKeys.push(treeItemKey);
          }

          if (!isEmpty(treeItem)) {
            flattenTreeFunc(treeItem, initialObj);
          }
        });

        return mergeWith(output, { [key]: treeItemKeys }, (obj1, obj2) => {
          if (!isArray(obj1)) {
            return obj2;
          }

          return obj1.concat(obj2);
        });
      }, initialObj);

      let flatResult = chain(this.localFilterTree)
        .mapValues(
          (localFilterGroup, filterKey) => {
            if (this.excludeFlatFilters.includes(filterKey)) {
              return cloneDeep(localFilterGroup);
            }
            return omitBy(flattenTreeFunc(localFilterGroup), isEmpty);
          },
        )
        .omitBy(isEmpty)
        .value();

      if (every(flatResult, isEmpty)) {
        flatResult = {};
      }

      const retVal = {
        ...this.modelValue,
        postloadFilter: flatResult,
        _localFilterUpdate: true,
      };
      if (isEqual(omit(this.modelValue, '_localFilterUpdate'), omit(retVal, '_localFilterUpdate'))) {
        return;
      }
      this.$emit('update:modelValue', retVal);
    }, // onUpdateFilter()

    onSwitchFilter(filterPath, filterState) {
      if (!filterState && this.isInitProgramFilter(filterPath)) {
        return;
      }

      const localFilter = this.localFilterTree;

      if (filterState) {
        set(localFilter, filterPath, {});
      } else {
        unset(localFilter, filterPath);
      }

      this.localFilterTree = { ...localFilter };
    }, // onSwitchFilter()

    async onLoadGroupByOptions() {
      if (!((this.dataSource?.groupByOptions ?? [])
        .some((groupByOption) => typeof groupByOption.expand === 'function'))) {
        this.cachedGroupByOptions = this.dataSource?.groupByOptions ?? [];
        return;
      }
      this.groupBySpinner = true;
      try {
        const expandGroupByOptionActions = chain(this.dataSource.groupByOptions)
          .flatMap(async (groupByOption) => {
            if (typeof groupByOption.expand !== 'function') {
              return [groupByOption];
            }
            const expandedGroupByOptions = await groupByOption.expand(this.modelValue);
            return map(
              expandedGroupByOptions,
              (expandedGroupByOption) => (
                {
                  ...groupByOption,
                  ...expandedGroupByOption,
                }
              ),
            );
          })
          .value();

        this.cachedGroupByOptions = flatten(await Promise.all(expandGroupByOptionActions));
      } finally {
        this.groupBySpinner = false;
      }
    },

    async onLoadPhaseLines(phaseLineScope) {
      if (this.isPhaseLineScopeLoaded(phaseLineScope.scope, phaseLineScope.program, phaseLineScope.target)) {
        // Already loaded...
        return;
      }
      const refreshPhaseLine = chain(phaseLineScope)
        .pick(['scope', 'program', 'target'])
        .set('client', this.currentClient.id)
        .value();
      this.$emit('refresh-phase-lines', [refreshPhaseLine]);
    }, // onLoadPhaseLines()

    async onUpdatePhaseLineAnnotations() {
      this.phaseLineAnnotationsLoading = true;
      this.$q.loading.show();
      try {
        this.phaseLineScopes = await this.getPhaseLineScopesFromFilter();
      } finally {
        this.phaseLineAnnotationsLoading = false;
        this.$q.loading.hide();
      }
    }, // onUpdatePhaseLineAnnotations()

    async onAddPhaseLine() {
      // Show dialog and receive new phase line
      const phaseLine = {
        id: null,
        label: '',
        startTimestamp: '',
        scope: null,
      };
      this.openComponentDialog(
        defineAsyncComponent(() => import('components/Insights/EditPhaseLine')),
        {
          phaseLine,
          scopes: this.phaseLineScopes,
          setDefaultScope: true,
        },
        (newPhaseLine) => {
          if (get(newPhaseLine, 'scope', 'local') === 'local') {
            this.$emit('update:modelValue', {
              ...this.modelValue,
              phaseLines: applyLineChange(this.modelValue.phaseLines, newPhaseLine),
            });
          } else {
            this.onSelectLineScope(
              SCOPED_PHASE_LINES,
              newPhaseLine.scope,
              newPhaseLine.program,
              newPhaseLine.target,
              true,
            );
            const refreshPhaseLine = chain(newPhaseLine)
              .pick(['scope', 'program', 'target', 'editStatus'])
              .set('client', this.currentClient.id)
              .value();
            this.$emit('refresh-phase-lines', [refreshPhaseLine]);
          }
        },
      );
    }, // onAddPhaseLine()

    onAddEventLine() {
      this.openEventLineDialog(
        {
          id: null,
          label: '',
          startTimestamp: '',
          scope: null,
        },
        true,
      );
    }, // onAddEventLine()

    onEditEventLine(eventLine) {
      this.openEventLineDialog(eventLine, false);
    }, // onEditEventLine()

    openEventLineDialog(eventLine, setDefaultScope) {
      this.openComponentDialog(
        defineAsyncComponent(() => import('components/Insights/EditEventLine')),
        {
          eventLine,
          scopes: this.phaseLineScopes,
          setDefaultScope,
        },
        (changedLine) => {
          if ((changedLine.scope ?? 'local') === 'local') {
            this.$emit('update:modelValue', {
              ...this.modelValue,
              eventLines: applyLineChange(this.modelValue.eventLines, changedLine),
            });
            return;
          }
          this.onSelectLineScope(
            SCOPED_EVENT_LINES,
            changedLine.scope,
            changedLine.program,
            changedLine.target,
            true,
          );
          const { scope, program, target } = changedLine;
          this.$emit('refresh-phase-lines', [{
            scope, program, target, client: this.currentClient.id,
          }]);
        },
      );
    }, // openEventLineDialog()

    async onEditPhaseLine(phaseLine) {
      // Show dialog and receive new phase line
      this.openComponentDialog(
        defineAsyncComponent(() => import('components/Insights/EditPhaseLine')),
        {
          phaseLine,
          scopes: this.phaseLineScopes,
        },
        (newPhaseLine) => {
          if (get(newPhaseLine, 'scope', 'local') === 'local') {
            this.$emit('update:modelValue', {
              ...this.modelValue,
              phaseLines: applyLineChange(this.modelValue.phaseLines, newPhaseLine),
            });
          } else {
            this.onSelectLineScope(
              SCOPED_PHASE_LINES,
              newPhaseLine.scope,
              newPhaseLine.program,
              newPhaseLine.target,
              true,
            );
            const refreshPhaseLine = chain(newPhaseLine)
              .pick(['scope', 'program', 'target'])
              .set('client', this.currentClient.id)
              .value();
            this.$emit('refresh-phase-lines', [refreshPhaseLine]);
          }
        },
      );
    }, // onEditPhaseLine()

    onSelectLineScope(lineType, scope, program, target, isSelected) {
      this.$emit('update:modelValue', {
        ...this.modelValue,
        phaseLines: setLineScopeSwitch(
          this.modelValue.phaseLines,
          this.getPhaseLineScopeFilter(scope, program, target),
          lineType,
          isSelected,
        ),
      });
    }, // onSelectLineScope()

    async onShowUsersFilter() {
      this.usersSpinner = true;

      try {
        const users = await this.usersFunc();
        this.sortedUsers = sortBy(users, 'userName');
      } finally {
        this.usersSpinner = false;
      }
    }, // onShowUsersFilter()

    async onShowAssessmentTypesFilter() {
      if ((this.assessmentTypes?.length ?? 0) > 0) {
        return;
      }

      this.assessmentTypesSpinner = true;
      try {
        await this.loadAssessmentTypes();
      } finally {
        this.assessmentTypesSpinner = false;
      }
    }, // onShowAssessmentTypesFilter()

    onShowAssessmentTypeFilter(assessmentTypeId) {
      return this.cacheAssessmentTypeFields(assessmentTypeId);
    }, // onShowAssessmentTypeFilter()

    async onShowAssessmentsFilter() {
      return this.cacheAssessments();
    }, // onShowAssessmentsFilter()

    async onShowAssessmentTypeDimensionDataFilter(assessmentTypeDimension) {
      return this.cacheAssessmentTypeFieldOptions(assessmentTypeDimension.id);
    }, // onShowAssessmentTypeDimensionDataFilter()

    async onShowProgramTargets(programId) {
      this.loadProgramTargets(programId);
    }, // onShowProgramTargets()

    async loadProgramTargets(programId) {
      if (!isEmpty(get(this.programTargets, programId))) {
        return;
      }

      this.$q.loading.show();

      await this.getTargets(programId);

      this.$q.loading.hide();
    }, // loadProgramTargets()

    async getTargets(programId) {
      let programTargetsRaw = await this.getAllProgramTargets({ program: programId });

      if (this.initDraftProgramTargets.length && programId === this.initProgramId) {
        programTargetsRaw = this.syncDBTargetsAndFrontendTargets(programTargetsRaw);
      }
      const programFilter = this.sortedPrograms.find((program) => program.id === programId)?.filter;
      const subtargetFilter = (id, isParent) => {
        return [
          ...programFilter,
          ...(isParent ? ['parentTargetId', id] : ['targetId', id]),
        ];
      };
      const innerSubtargetFilter = (id, parentId, isParent) => {
        return [
          ...programFilter,
          ...(isParent ? ['parentTargetId', id] : ['parentTargetId', parentId, 'targetId', id]),
        ];
      };

      this.programTargets[programId] = programTargetsRaw
        .filter((target) => !target.parentTarget).map((parentTarget) => {
          const subTargets = programTargetsRaw
            .filter((target) => target.parentTarget === parentTarget.id)
            .map((subTarget) => {
              const innerSubTargets = programTargetsRaw.filter((target) => target.parentTarget === subTarget.id);
              return {
                ...subTarget,
                filter: innerSubtargetFilter(subTarget.id, subTarget.parentTarget, innerSubTargets.length),
                ...(innerSubTargets.length ? { subTargets: innerSubTargets } : {}),
              };
            });
          return {
            ...parentTarget,
            filter: subtargetFilter(parentTarget.id, subTargets.length),
            subTargets,
          };
        });

      return programTargetsRaw;
    }, // getTargets()

    async cacheAssessments() {
      if ((this.assessments?.length ?? 0) > 0) {
        return;
      }
      this.assessmentsSpinner = true;
      try {
        await this.loadAssessments();
      } finally {
        this.assessmentsSpinner = false;
      }
    }, // cacheAssessments()

    async cacheAssessmentTypeFields(assessmentTypeId) {
      this.assessmentTypeSpinners[assessmentTypeId] = true;
      try {
        await this.getAssessmentFormFields(assessmentTypeId);
        this.assessmentTypeFieldsCache[assessmentTypeId] = chain(this.assessmentTypeFields)
          .map((field) => {
            const fieldGroup = find(this.assessmentTypeFieldGroups, { id: field.assessmentTypeFieldGroup });
            return {
              order: (fieldGroup?.order ?? 0) * 1000 + field.order,
              label: field.label,
              isMetric: field.isMetric,
              isDimension: field.isDimension,
              id: field.id,
            };
          })
          .sortBy('order')
          .value();
      } finally {
        this.assessmentTypeSpinners[assessmentTypeId] = false;
      }
    }, // cacheAssessmentTypeFields()

    async cacheAssessmentTypeFieldOptions(assessmentTypeDimensionId) {
      if (!isEmpty(this.assessmentTypeFieldOptionsCache[assessmentTypeDimensionId])) {
        return;
      }

      this.assessmentDataSpinners[assessmentTypeDimensionId] = true;
      try {
        const fieldOptions = await this.readDataFieldOptions(
          {
            field: assessmentTypeDimensionId,
            clientId: this.currentClient.id,
          },
        );
        this.assessmentTypeFieldOptionsCache[assessmentTypeDimensionId] = fieldOptions;
      } finally {
        this.assessmentDataSpinners[assessmentTypeDimensionId] = false;
      }
    }, // cacheAssessmentTypeFieldOptions()

    /**
     * on the editProgram page, you can modify target data or create a new target, and until saved, these changes are
     * stored only on the frontend. To ensure the graph always has up-to-date targets data for the current program,
     * it is necessary to synchronize the data received from the database with the data stored on the frontend.
     */
    syncDBTargetsAndFrontendTargets(dbTargets) {
      const initTargetsMap = keyBy(this.initDraftProgramTargets, 'id');

      const updatedTargets = dbTargets.map((dbTarget) => {
        const target = initTargetsMap[dbTarget.id];
        return target ? { ...dbTarget, ...target } : dbTarget;
      });

      const newTargets = this.initDraftProgramTargets.filter((target) => {
        return get(target, 'editStatus', '') === 'create';
      });

      return [...updatedTargets, ...newTargets];
    },

    hasFilter(filterPath) {
      return chain(this.dataSource).get('availableFilters').includes(filterPath).value();
    }, // hasFilter()

    hasFiltersApplied(filterPath) {
      return !isEmpty(get(this.localFilterTree, filterPath));
    }, // hasFiltersApplied()

    filterValue(filterPath, nextLevel = false) {
      if (!has(this.localFilterTree, filterPath)) {
        return false;
      }

      if (nextLevel
        && chain(this.localFilterTree)
          .get(filterPath)
          .map((deepProps) => (isObject(deepProps) && !isEmpty(deepProps)))
          .some()
          .value()) {
        return undefined;
      }

      return true;
    }, // filterValue()

    isInitProgram(program) {
      return !!this.initProgramId && program.id === this.initProgramId;
    }, // isInitProgram()

    // Saved filters can hold a malformed (non-array) programId value, so normalise before use.
    resolveEnforcedProgramIds(programIds) {
      const currentProgramIds = Array.isArray(programIds) ? programIds : [];
      if (!this.initProgramId || currentProgramIds.includes(this.initProgramId)) {
        return null;
      }
      return [...currentProgramIds, this.initProgramId];
    }, // resolveEnforcedProgramIds()

    buildInitialProgramRepair(value) {
      const programIds = get(value, ['postloadFilter', 'programs', 'programId'], []);
      const enforcedProgramIds = this.resolveEnforcedProgramIds(programIds);
      if (!enforcedProgramIds) {
        return null;
      }
      const savedFilter = value?.postloadFilter;
      const isFilterObject = !!savedFilter && typeof savedFilter === 'object' && !Array.isArray(savedFilter);
      const postloadFilter = isFilterObject ? cloneDeep(savedFilter) : {};
      set(postloadFilter, ['programs', 'programId'], enforcedProgramIds);
      return { ...value, postloadFilter };
    }, // buildInitialProgramRepair()

    isInitProgramFilter(filterPath) {
      // A program-level filter path is always ['programs', 'programId', <programId>].
      return !!this.initProgramId
        && filterPath.length === 3
        && filterPath[0] === 'programs'
        && filterPath[1] === 'programId'
        && filterPath[2] === this.initProgramId;
    }, // isInitProgramFilter()

    isLineScopeSelected(lineType, scope, program = null, target = null) {
      const scopeFilter = this.getPhaseLineScopeFilter(scope, program, target);

      return this.phaseLines.find(matchesLineScope(scopeFilter))?.[lineType.switchKey] ?? false;
    }, // isLineScopeSelected()

    isPhaseLineScopeLoaded(scope, program = null, target = null) {
      const scopeFilter = this.getPhaseLineScopeFilter(scope, program, target);
      return chain(this.chartPhaseLineScopes).find(scopeFilter).get('isLoaded', false).value();
    }, // isPhaseLineScopeSelected()

    hasScopedPhaseLines(scope, program = null, target = null) {
      return size(this.getLinesInScope(SCOPED_PHASE_LINES, scope, program, target)) > 0;
    }, // hasScopedPhaseLines()

    getAssessments(assessmentType) {
      return chain(this.assessments)
        .filter({ assessmentType })
        .sortBy((assessment) => (assessment.dateCompleted instanceof Timestamp
          ? assessment.dateCompleted.toDate()
          : null))
        .reverse()
        .value();
    }, // getAssessments()

    getAssessmentTypeDimensions(assessmentTypeId) {
      return chain(this.assessmentTypeFieldsCache)
        .get(assessmentTypeId)
        .filter({ isDimension: true })
        .value();
    }, // getAssessmentTypeDimensions()

    getAssessmentTypeMetrics(assessmentTypeId) {
      return chain(this.assessmentTypeFieldsCache)
        .get(assessmentTypeId)
        .filter({ isMetric: true })
        .value();
    }, // getAssessmentTypeMetrics()

    getLineGroupScopes(lineType) {
      return this.phaseLineScopes.map((phaseLineScope) => {
        const { scope, program, target } = phaseLineScope;
        return {
          ...phaseLineScope,
          caption: this.$t(`insights.chartConfigurator.phaseLineTypes.${scope}`),
          isSelected: this.isLineScopeSelected(lineType, scope, program, target),
          isLoaded: this.isPhaseLineScopeLoaded(scope, program, target),
          lines: this.getLinesInScope(lineType, scope, program, target),
        };
      });
    }, // getLineGroupScopes()

    getLinesInScope(lineType, scope, program = null, target = null) {
      const scopeFilter = this.getPhaseLineScopeFilter(scope, program, target);
      return this.chartPhaseLineScopes.find(matchesLineScope(scopeFilter))?.[lineType.linesKey] ?? [];
    }, // getLinesInScope()

    async getPhaseLineScopesFromFilter() {
      const clientScope = [{
        value: 'global',
        label: this.currentClient.clientName,
        scope: 'global',
        order: 0,
      }];

      const programIds = get(this.modelValue, ['postloadFilter', 'programs', 'programId'], []);
      const programScopes = chain(this.sortedPrograms)
        .filter((program) => programIds.includes(program.id))
        .map((program, index) => ({
          value: program.id,
          label: program.programName,
          scope: 'program',
          order: (index + 1) * 10000000,

          program: program.id,
          target: null,
        }))
        .value();

      if (!isEmpty(programScopes)) {
        const loadProgramTargetsActions = map(programScopes, (program) => this.loadProgramTargets(program.value));
        await Promise.all(loadProgramTargetsActions);

        const targetId = get(this.modelValue, ['postloadFilter', 'programs', 'targetId'], []);
        const parentTargetId = get(this.modelValue, ['postloadFilter', 'programs', 'parentTargetId'], []);

        const targetScopes = chain(programScopes)
          .map((programScope) => chain(this.programTargets)
            .get(programScope.value)
            .filter((programTarget) => {
              const isSubTargetIdMatches = !!(programTarget.subTargets.find((subTarget) => {
                return (targetId.includes(subTarget.id) || parentTargetId.includes(subTarget.id));
              }));

              return (targetId.includes(programTarget.id) || parentTargetId.includes(programTarget.id))
                  || isSubTargetIdMatches;
            })
            .map((programTarget) => ({
              value: programTarget.id,
              label: programTarget.label,
              scope: 'target',
              order: programScope.order + programTarget.order * 10000,

              program: programScope.value,
              target: programTarget.id,
            }))
            .value())
          .flatten()
          .value();

        const subTargetScopes = chain(targetScopes)
          .map((targetScope) => chain(this.programTargets)
            .get(targetScope.program)
            .find({ id: targetScope.value })
            .get('subTargets')
            .filter((programTarget) => targetId.includes(programTarget.id)
            // The first ID in the parentTargetId array will always be the ID of the active(selected) target.
            // Need to search only by the first target ID, so that sub-target phase line switches don't appear
            // in this target Phase line selector
              || parentTargetId[0]?.includes(programTarget.id))
            .map((programTarget) => ({
              value: programTarget.id,
              label: programTarget.label,
              scope: 'subTarget',
              order: targetScope.order + programTarget.order,

              program: targetScope.program,
              target: programTarget.id,
            }))
            .value())
          .flatten()
          .value();

        return chain(clientScope).concat(programScopes, targetScopes, subTargetScopes).sortBy('order').value();
      }

      return clientScope;
    }, // getPhaseLineScopesFromFilter()
  },

};
</script>
